/**
 * Buddy API Service - NEW Implementation
 * Handles all buddy system API calls for facilitator-based matching
 */

import api from './api';

class BuddyService {
  // ==================== FACILITATOR DISCOVERY ====================
  
  /**
   * Get list of available facilitators
   */
  async getFacilitators() {
    const response = await api.get('/buddy/facilitators');
    return response.data;
  }

  /**
   * Get details of a specific facilitator
   */
  async getFacilitatorDetails(facilitatorId) {
    const response = await api.get(`/buddy/facilitators/${facilitatorId}`);
    return response.data;
  }

  // ==================== MATCH REQUESTS ====================
  
  /**
   * Send a match request to a facilitator
   */
  async sendMatchRequest(data) {
    const response = await api.post('/buddy/requests', data);
    return response.data;
  }

  /**
   * Get all my match requests
   */
  async getMyRequests() {
    const response = await api.get('/buddy/requests');
    return response.data;
  }

  /**
   * Cancel a pending match request
   */
  async cancelRequest(requestId) {
    const response = await api.delete(`/buddy/requests/${requestId}`);
    return response.data;
  }

  // ==================== FACILITATOR DASHBOARD ====================
  
  /**
   * Check if current user is a facilitator
   */
  async checkFacilitatorStatus() {
    const response = await api.get('/buddy/facilitator/check');
    return response.data;
  }
  
  /**
   * Get incoming match requests (for facilitators)
   */
  async getFacilitatorRequests(status = 'pending') {
    const response = await api.get('/buddy/facilitator/requests', {
      params: { status }
    });
    return response.data;
  }

  /**
   * Accept a match request (for facilitators)
   */
  async acceptRequest(requestId, message = '') {
    const response = await api.post(`/buddy/facilitator/requests/${requestId}/accept`, {
      message
    });
    return response.data;
  }

  /**
   * Reject a match request (for facilitators)
   */
  async rejectRequest(requestId, message = '') {
    const response = await api.post(`/buddy/facilitator/requests/${requestId}/reject`, {
      message
    });
    return response.data;
  }

  /**
   * Get all active matches (for facilitators)
   */
  async getFacilitatorMatches() {
    const response = await api.get('/buddy/facilitator/matches');
    return response.data;
  }

  /**
   * Get facilitator statistics
   */
  async getFacilitatorStats() {
    const response = await api.get('/buddy/facilitator/stats');
    return response.data;
  }

  // ==================== CURRENT MATCH ====================
  
  /**
   * Get current active buddy match
   */
  async getCurrentMatch() {
    const response = await api.get('/buddy/current');
    return response.data;
  }

  /**
   * Deactivate a buddy match
   */
  async deactivateMatch(matchId) {
    const response = await api.post(`/buddy/match/${matchId}/deactivate`);
    return response.data;
  }

  // ==================== INTERACTIONS ====================
  
  /**
   * Log an interaction with buddy
   */
  async logInteraction(data) {
    const response = await api.post('/buddy/interactions', data);
    return response.data;
  }

  /**
   * Get interaction history
   */
  async getInteractions(matchId) {
    const response = await api.get('/buddy/interactions', {
      params: { match_id: matchId }
    });
    return response.data;
  }

  // ==================== SESSIONS (Legacy - kept for compatibility) ====================
  
  /**
   * Get buddy sessions
   */
  async getSessions(status = null) {
    const response = await api.get('/buddy/sessions', {
      params: status ? { status } : {}
    });
    return response.data;
  }

  /**
   * Book a buddy session
   */
  async bookSession(data) {
    const response = await api.post('/buddy/sessions', data);
    return response.data;
  }

  /**
   * Update a session
   */
  async updateSession(sessionId, data) {
    const response = await api.put(`/buddy/sessions/${sessionId}`, data);
    return response.data;
  }

  /**
   * Cancel a session
   */
  async cancelSession(sessionId) {
    const response = await api.delete(`/buddy/sessions/${sessionId}`);
    return response.data;
  }

  /**
   * Get session quota
   */
  async getQuota() {
    const response = await api.get('/buddy/quota');
    return response.data;
  }
}

export default new BuddyService();
